import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatCFA(amount: number): string {
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount) + ' FCFA'
}

export function formatDate(date: string | Date): string {
  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(date))
}

export function formatRelativeTime(date: string | Date): string {
  const now = new Date()
  // Tolérance : certains endpoints renvoient le format MySQL « YYYY-MM-DD
  // HH:MM:SS » (espaces), que `new Date()` n'analyse pas de façon fiable sur
  // tous les navigateurs. On normalise en ISO 8601 (« T ») avant le parse.
  const normalized = typeof date === 'string' && date.includes(' ') && !date.includes('T')
    ? date.replace(' ', 'T')
    : date
  const d = new Date(normalized)
  const diff = Math.floor((now.getTime() - d.getTime()) / 1000)

  if (diff < 60) return `il y a ${diff}s`
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)}min`
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`
  return `il y a ${Math.floor(diff / 86400)}j`
}

/**
 * Temps restant avant une échéance future, en français court (« 3 j 4 h »).
 * Utilisé pour l'expiration du jeton de session ; renvoie « expiré » lorsque
 * l'échéance est dépassée.
 */
export function formatCountdown(date: string | Date): string {
  const normalized = typeof date === 'string' && date.includes(' ') && !date.includes('T')
    ? date.replace(' ', 'T')
    : date
  const target = new Date(normalized).getTime()

  if (!Number.isFinite(target)) return '—'

  const remaining = target - Date.now()

  if (remaining <= 0) return 'expiré'

  const days = Math.floor(remaining / 86400000)
  const hours = Math.floor((remaining % 86400000) / 3600000)

  if (days > 0) return `${days} j ${hours} h`

  const minutes = Math.floor((remaining % 3600000) / 60000)

  return `${hours} h ${minutes} min`
}
